import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import { ApiService } from "../services/api";
import AuthService from "../services/authService";

interface PhotoViewerModalProps {
  open: boolean;
  invoiceId: number;
  onClose: () => void;
  onPhotoDeleted?: () => void;
}

export const PhotoViewerModal: React.FC<PhotoViewerModalProps> = ({
  open,
  invoiceId,
  onClose,
  onPhotoDeleted,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);

  const photoUrl = ApiService.getInvoicePhotoUrl(invoiceId);

  // Load image with authentication
  useEffect(() => {
    let mounted = true;
    let blobUrl: string | null = null;

    const loadImage = async () => {
      if (!open) return;

      setIsLoading(true);
      setImageError(false);
      setIsPdf(false);

      try {
        const token = AuthService.getToken();
        const response = await fetch(photoUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }

        const blob = await response.blob();
        const contentType = response.headers.get("content-type") || blob.type;
        blobUrl = window.URL.createObjectURL(blob);

        if (mounted) {
          setIsPdf(contentType.includes("pdf"));
          setImageBlobUrl(blobUrl);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error loading photo:", error);
        if (mounted) {
          setImageError(true);
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
    };
  }, [open, invoiceId, photoUrl]);

  const handleDownload = async () => {
    if (!imageBlobUrl) return;

    setIsDownloading(true);
    try {
      const link = document.createElement("a");
      link.href = imageBlobUrl;
      link.download = `fatura-${invoiceId}.${isPdf ? "pdf" : "jpg"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading photo:", error);
      alert("Erro ao baixar foto. Por favor tente novamente.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Tem certeza que deseja deletar esta foto?")) {
      return;
    }

    setIsDeleting(true);
    try {
      await ApiService.deleteInvoicePhoto(invoiceId);
      onPhotoDeleted?.();
      onClose();
    } catch (error) {
      console.error("Error deleting photo:", error);
      alert("Erro ao deletar foto. Por favor tente novamente.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6">{isPdf ? "Documento da Fatura" : "Foto da Fatura"}</Typography>
        <Box>
          <IconButton
            onClick={handleDownload}
            disabled={isDownloading || imageError}
            color="primary"
            sx={{ mr: 1 }}
            title="Baixar foto"
          >
            {isDownloading ? <CircularProgress size={24} /> : <DownloadIcon />}
          </IconButton>
          <IconButton
            onClick={handleDelete}
            disabled={isDeleting}
            color="error"
            sx={{ mr: 1 }}
            title="Deletar foto"
          >
            {isDeleting ? <CircularProgress size={24} /> : <DeleteIcon />}
          </IconButton>
          <IconButton onClick={onClose} title="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          backgroundColor: "#000",
        }}
      >
        {isLoading ? (
          <CircularProgress sx={{ color: "#fff" }} />
        ) : imageError ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              p: 4,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Erro ao carregar imagem
            </Typography>
            <Typography variant="body2">
              A imagem não pôde ser carregada
            </Typography>
          </Box>
        ) : imageBlobUrl ? (
          isPdf ? (
            <iframe
              src={imageBlobUrl}
              title="Documento da fatura"
              style={{
                width: "100%",
                height: "70vh",
                border: "none",
              }}
            />
          ) : (
            <img
              src={imageBlobUrl}
              alt="Foto da fatura"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          )
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotoViewerModal;
